import ExcelJS from 'exceljs';
import { ICostDetail, ITimeDetail } from '../types';
import i18next from "i18next";
import { MonthNumber } from '../../../constants/MonthNumber';
import { transformData } from './formatValue';
import groupByMonthDescending from '../../../functions/groupByMonthDescending';

const accentColor = "EF3129", fontName = "Calibri";

/**
 * Offset of a table's last column from its first. The hours table carries a
 * booked range on top of the shared columns, the costs table does not, so the
 * two are no longer the same width.
 *
 * Hours: No | Date | Project Num | Project description | Task | Time | Hours
 * Costs: No | Date | Project Num | Project description | Description | Costs
 */
const lastColumn = { Hours: 6, Costs: 5 } as const;

/** One empty column separates the blocks laid out across the sheet. */
const blockGap = 1;

const nextBlock = (col: number, type: "Hours" | "Costs") =>
	col + lastColumn[type] + 1 + blockGap;

export function columnNumberToLetter(col: number): string {
	let letter = '';
	while (col > 0) {
		let temp = (col - 1) % 26;
		letter = String.fromCharCode(temp + 65) + letter;
		col = Math.floor((col - 1) / 26);
	}
	return letter;
}

type DataMap = Record<string, number>;

// Особенная функция для выравнивания двух массивов по которым отрисовываются таблицы time и cost (те что с конкретным периодом по типу February, 2025)
const alignDateObjects = (obj1: DataMap, obj2: DataMap): [DataMap, DataMap] => {
	// Копии, чтобы не мутировать оригиналы
	const updatedObj1: DataMap = { ...obj1 };
	const updatedObj2: DataMap = {};

	// Получаем отсортированные ключи по дате
	const sortedKeys = Object.keys(updatedObj1).sort();

	// Если первый объект не пустой
	if(sortedKeys.length) {
		for (let i = 0; i < sortedKeys.length; i++) {
			const key = sortedKeys[i];
	
			if (key in obj2) {
				const diff = obj2[key] - updatedObj1[key];
	
				if (diff > 0) {
					// Применяем diff ко всем последующим ключам (включая текущий)
					for (let j = i; j < sortedKeys.length; j++) {
						const k = sortedKeys[j];
						updatedObj1[k] += diff;
					}
				}
			}
		}
	
		// Строим второй объект на основе обновлённого первого (сохраняя только нужные ключи)
		for (const key of Object.keys(obj2)) {
			if (key in updatedObj1) {
				updatedObj2[key] = updatedObj1[key];
			}
		}

		return [updatedObj1, updatedObj2];
	} else {
		const updatedObj1: DataMap = {};
		const updatedObj2: DataMap = { ...obj2 };

		return [updatedObj1, updatedObj2];
	}
};

// Функция для получения предыдущего элемента в объекте
function getPreviousEntry(obj: Record<string, number>, currentKey: string): [string, number] | null {
	const sortedKeys = Object.keys(obj).sort(); // сортируем по дате (строки вида YYYY-MM)

	const index = sortedKeys.indexOf(currentKey);

	if (index > 0) {
		const prevKey = sortedKeys[index - 1];
		return [prevKey, obj[prevKey]];
	}

	return null; // нет предыдущего
}

// Функция для создания таблиц
export const createEmployeeTable = (worksheet: ExcelJS.Worksheet, data: { time: ITimeDetail[], cost: ICostDetail[] }) => {
	const { t } = i18next, tr = (field: string) => t("analytics_export." + field);
	const formatHours = 1, formatCost = 2;

	const headingText = { name: fontName, size: 11, bold: true };

	// Написания заголовка
	worksheet.mergeCells('B2:H2');
	worksheet.getCell('B2').value = tr("title");
	worksheet.getCell('B2').font = { name: fontName, size: 22, bold: true };
	worksheet.getCell('B2').alignment = { horizontal: "left", vertical: 'middle' };

	// Функция для отрисовки обводки
	function setBorder(row: number, col: number) {
		worksheet.getCell(row, col).border = {
			top: { style: 'thin', color: { argb: '000000' } },
			left: { style: 'thin', color: { argb: '000000' } },
			bottom: { style: 'thin', color: { argb: '000000' } },
			right: { style: 'thin', color: { argb: '000000' } },
		};
	}

	// Функция для отрисовки обводки (точками)
	function setBorderCells(row: number, col: number, counter: number, max: number, type: "Hours" | "Costs") {
		const isLast = counter === max,
			maxIndex = lastColumn[type];

		if (isLast) {
			for (let index = 0; index <= maxIndex; index++) {
				if (index === 0) {
					worksheet.getCell(row, col + index).border = {
						left: { style: "thin", color: { argb: '000000' } },
						top: { style: "hair", color: { argb: '000000' } },
						right: { style: "hair", color: { argb: '000000' } },
						bottom: { style: "thin", color: { argb: '000000' } }
					}
				} else if (index === maxIndex) {
					worksheet.getCell(row, col + index).border = {
						left: { style: "hair", color: { argb: '000000' } },
						right: { style: "thin", color: { argb: '000000' } },
						top: { style: "hair", color: { argb: '000000' } },
						bottom: { style: "thin", color: { argb: '000000' } }
					}
				} else {
					worksheet.getCell(row, col + index).border = {
						top: { style: "hair", color: { argb: '000000' } },
						left: { style: "hair", color: { argb: '000000' } },
						right: { style: "hair", color: { argb: '000000' } },
						bottom: { style: "thin", color: { argb: '000000' } },
					}
				}
			}
		} else {
			worksheet.getCell(row, col).border = {
				left: { style: "thin", color: { argb: '000000' } },
				top: { style: "hair", color: { argb: '000000' } },
				bottom: { style: "hair", color: { argb: '000000' } },
				right: { style: "hair", color: { argb: '000000' } }
			}

			worksheet.getCell(row, col + maxIndex).border = {
				right: { style: "thin", color: { argb: '000000' } },
				top: { style: "hair", color: { argb: '000000' } },
				bottom: { style: "hair", color: { argb: '000000' } },
				left: { style: "hair", color: { argb: '000000' } }
			}
		}
	}

	// Функция для формирования акцентной ячейки (для head ячейки)
	function setAccent(value: string, row: number, col: number) {
		const cell = worksheet.getCell(row, col);
		cell.font = { name: fontName, size: 11, bold: true, color: { argb: "FFFFFF" } };
		cell.value = value;
		cell.alignment = { horizontal: "center", vertical: 'middle' };
		cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
		cell.border = {
			top: { style: 'thin', color: { argb: '000000' } },
			left: { style: 'thin', color: { argb: '000000' } },
			bottom: { style: 'thin', color: { argb: '000000' } },
			right: { style: 'thin', color: { argb: '000000' } }
		};
	}

	// Функция для записывания данных в ячейку
	function setField(value: string | number | Date, row: number, col: number, isLeft?: boolean) {
		const cell = worksheet.getCell(row, col);
		cell.font = { name: fontName, size: 10 };
		cell.value = value;
		if (value as number) {
			cell.numFmt = "dd.mm.yy";
		}
		cell.alignment = { wrapText: true, horizontal: isLeft ? "left" : "center", vertical: "middle" };
		cell.border = {
			top: { style: "hair", color: { argb: '000000' } },
			left: { style: "hair", color: { argb: '000000' } },
			bottom: { style: "hair", color: { argb: '000000' } },
			right: { style: "hair", color: { argb: '000000' } }
		};
	}

	// Функция для записывания данных в ячейку (числом)
	function setNumberField(value: number, row: number, col: number, type?: "Hours" | "Costs") {
		const cell = worksheet.getCell(row, col);
		cell.font = { name: fontName, size: 10 };
		cell.value = value;

		if (type) cell.numFmt = `0.${"0".repeat(type === "Hours" ? formatHours : formatCost)}`;

		cell.alignment = { horizontal: "center", vertical: "middle" };
		cell.border = {
			top: { style: "hair", color: { argb: '000000' } },
			left: { style: "hair", color: { argb: '000000' } },
			bottom: { style: "hair", color: { argb: '000000' } },
			right: { style: "hair", color: { argb: '000000' } }
		};
	}

	// Функция для записывания формулы в ячейку
	function setFormulaField(args: {
		value: number
		formula: {
			row: {
				start: number
				end: number
			}
			col: {
				start: number
				end: number
			}
		}
		row: number
		col: number
		type: "Hours" | "Costs"
		isAccent?: boolean
	}) {
		const { value, row, col, formula, type, isAccent } = args;

		const startCellLetter = columnNumberToLetter(formula.col.start),
			endCellLetter = columnNumberToLetter(formula.col.end);

		const cell = worksheet.getCell(row, col);
		cell.font = isAccent ? { name: fontName, size: 11, bold: true, color: { argb: accentColor } } : { name: fontName, size: 10 };
		cell.value = { formula: `SUM(${startCellLetter}${formula.row.start}:${endCellLetter}${formula.row.end})`, result: String(value), date1904: false };
		cell.numFmt = `0.${"0".repeat(type === "Hours" ? formatHours : formatCost)}`;
		cell.alignment = { horizontal: "center", vertical: "middle" };
		cell.border = {
			top: { style: "hair", color: { argb: '000000' } },
			left: { style: "hair", color: { argb: '000000' } },
			bottom: { style: "hair", color: { argb: '000000' } },
			right: { style: "hair", color: { argb: '000000' } }
		};
	}

	// Функция для формирования общих данных таблицы
	function setTotal(args: {
		row: number
		col: number
		formula: {
			row: {
				start: number
				end: number
			}
			col: {
				start: number
				end: number
			}
		},
		type: "Hours" | "Costs",
		data: {
			time: ITimeDetail[]
			cost: ICostDetail[]
		}
	}) {
		const { col, row, formula, data, type } = args;

		//worksheet.getRow(row).height = 20;

		const maxCol = lastColumn[type];

		const totalTitleCell = worksheet.getCell(row, col + maxCol - 1),
			totalTypeCell = worksheet.getCell(row, col + maxCol);

		totalTitleCell.font = { name: fontName, size: 11, bold: true, color: { argb: accentColor } };
		totalTitleCell.alignment = { horizontal: "right", vertical: "middle" }
		totalTitleCell.value = tr("total") + ":";

		let sum = 0;
		if (type === "Hours") {
			data.time.forEach(item => {
				sum += item.hours;
			})
		} else if (type === "Costs") {
			data.cost.forEach(item => {
				sum += item.costs;
			})
		}

		setFormulaField({
			formula, type,
			value: sum,
			row, col: col + maxCol,
			isAccent: true,
		})

		/* totalTypeCell.font = { name: fontName, size: 11, bold: true, color: { argb: accentColor } };
		totalTypeCell.alignment = { horizontal: "center", vertical: "middle" }
		totalTypeCell.value = type === "Hours" ? t("summary_page.main.hours") : "UAH"; */

		worksheet.getCell(row, col).border = {
			left: { style: "thin", color: { argb: '000000' } },
			bottom: { style: "thin", color: { argb: '000000' } }
		};

		for (let index = 1; index <= maxCol - 1; index++) {
			worksheet.getCell(row, col + index).border = {
				bottom: { style: "thin", color: { argb: '000000' } }
			};
		}

		worksheet.getCell(row, col + maxCol).border = {
			bottom: { style: "thin", color: { argb: '000000' } },
			right: { style: "thin", color: { argb: '000000' } }
		};

	}

	// Функция для создания таблицы
	function createTable(args: {
		data: {
			time: ITimeDetail[]
			cost: ICostDetail[]
		}
		caption?: string
		period: "all" | string
		colStart: number
		rowStart: number
		type: "Hours" | "Costs"
		numberList: {
			time: {
				name: string
				number: number
			}[]
			cost: {
				name: string
				number: number
			}[]
		}
	}) {
		const { data, caption, period, colStart, rowStart, type, numberList } = args;

		const maxIndex = lastColumn[type];

		let nextRow = 0;

		worksheet.mergeCells([rowStart, colStart, rowStart, colStart + maxIndex]);

		if (caption) {
			setBorder(rowStart, colStart);
			const captionCell = worksheet.getCell(rowStart, colStart);
			captionCell.value = caption;
			captionCell.font = headingText;

			nextRow = nextRow + 1;

			worksheet.mergeCells([rowStart + nextRow, colStart, rowStart + nextRow, colStart + maxIndex]);
		}

		setBorder(rowStart + nextRow, colStart);
		const periodCell = worksheet.getCell(rowStart + nextRow, colStart);
		periodCell.value = period === "all" ? tr("all_period") : `${t(MonthNumber()[+period.split("-")[1]].translate_code)}, ${period.split("-")[0]}`;
		periodCell.font = headingText;
		periodCell.alignment = { vertical: "middle" }

		nextRow = nextRow + 1;

		const tableHead = {
			date: t("timesheet_page.table.date"),
			project_num: t("timesheet_page.table.project_num"),
			project_description: t("timesheet_page.table.project_description"),
			task: type === "Hours" ? t("timesheet_page.table.task") : t("costs_page.table.expense_description"),
			type: type === "Hours" ? t("timesheet_page.table.hours") : `${tr("Costs")} (UAH)`
		}

		setAccent("No", rowStart + nextRow, colStart);
		setAccent(tableHead.date, rowStart + nextRow, colStart + 1);
		setAccent(tableHead.project_num, rowStart + nextRow, colStart + 2);
		setAccent(tableHead.project_description, rowStart + nextRow, colStart + 3);
		setAccent(tableHead.task, rowStart + nextRow, colStart + 4);
		// The booked range sits between the task and its hours, as in a timesheet
		if (type === "Hours") setAccent(t("timesheet_page.table.time"), rowStart + nextRow, colStart + 5);
		setAccent(tableHead.type, rowStart + nextRow, colStart + maxIndex);

		if (type === "Hours" && period === "all" && data.time.length) {
			// Only this table's own columns and rows. Stretching the range as far
			// as the Costs table hung a filter button on the empty column between
			// the two, and a sheet carries just the one filter anyway.
			worksheet.autoFilter = {
				from: { row: rowStart + nextRow, column: colStart },
				to: { row: rowStart + nextRow + data.time.length, column: colStart + maxIndex }
			};
		}

		nextRow++;

		worksheet.getColumn(colStart).width = 5;
		worksheet.getColumn(colStart + 1).width = 13;
		worksheet.getColumn(colStart + 2).width = 13;
		worksheet.getColumn(colStart + 3).width = 18;
		worksheet.getColumn(colStart + 4).width = 35;
		// wide enough to also hold the "Total:" label that sits above the sum
		if (type === "Hours") worksheet.getColumn(colStart + 5).width = 15;
		worksheet.getColumn(colStart + maxIndex).width = type === "Hours" ? 8 : 11.5;

		if (type === "Hours" && data.time.length) {
			const filterData: Record<string, ITimeDetail[]> = {};

			const sortedData = [...data.time].sort((a, b) => {
				return new Date(a.date).getTime() - new Date(b.date).getTime();
			});

			sortedData.forEach(item => {
				if (!filterData[item.date]) filterData[item.date] = [];
				filterData[item.date].push(item);
			})

			let counter = 0;
			for (const key in filterData) {
				const currentCounter = counter;

				filterData[key].forEach((item, index) => {
					const number = numberList.time.filter(numberItem => numberItem.name === `${item.date}_${item.task}`)[0].number,
					rowIndex = rowStart + nextRow + currentCounter + index;

					setNumberField(number, 				rowIndex, colStart);
					setField(transformData(key), 		rowIndex, colStart + 1);
					setField(item.project_name, 		rowIndex, colStart + 2);
					setField(item.project_description, 	rowIndex, colStart + 3);
					setField(item.task, 				rowIndex, colStart + 4, true);
					setField(item.time || "-", 			rowIndex, colStart + 5);
					setNumberField(item.hours, 			rowIndex, colStart + maxIndex, "Hours");

					const row = rowStart + nextRow + currentCounter + index;
					setBorderCells(row, colStart, currentCounter + index + 1, data.time.length, type);

				});

				counter = counter + filterData[key].length;
			}

			setTotal({
				formula: {
					row: {
						start: rowStart + nextRow - 1,
						end: rowStart + nextRow - 1 + data.time.length
					},
					col: {
						start: colStart + maxIndex,
						end: colStart + maxIndex
					}
				},
				row: rowStart + nextRow + data.time.length, col: colStart,
				data, type
			})

		} else if (type === "Costs" && data.cost.length) {
			const filterData: Record<string, ICostDetail[]> = {};

			const sortedData = [...data.cost].sort((a, b) => {
				return new Date(a.date).getTime() - new Date(b.date).getTime();
			});

			sortedData.forEach(item => {
				if (!filterData[item.date]) filterData[item.date] = [];
				filterData[item.date].push(item);
			})

			let counter = 0;
			for (const key in filterData) {
				const currentCounter = counter;

				filterData[key].forEach((item, index) => {
					const number = numberList.cost.filter(numberItem => numberItem.name === `${item.date}_${item.description}`)[0].number;
					const rowIndex = rowStart + nextRow + currentCounter + index;

					setNumberField(number, rowIndex, colStart);

					setField(transformData(key), rowIndex, colStart + 1);

					setField(item.project_name, rowIndex, colStart + 2);
					setField(item.project_description, rowIndex, colStart + 3);
					setField(item.description, rowIndex, colStart + 4, true);
					setNumberField(item.costs, rowIndex, colStart + 5, "Costs");

					const row = rowStart + nextRow + currentCounter + index;
					setBorderCells(row, colStart, currentCounter + index + 1, data.cost.length, type);
				});

				counter = counter + filterData[key].length;

			}

			setTotal({
				formula: {
					row: {
						start: rowStart + nextRow,
						end: rowStart + nextRow - 1 + data.cost.length
					},
					col: {
						start: colStart + maxIndex,
						end: colStart + maxIndex
					}
				},
				row: rowStart + nextRow + data.cost.length, col: colStart,
				data, type
			})
		}
	}

	// Сортировка данных по дате (от самой старой - до самой новой)
	const sorted = {
		time: [...data.time].sort((a, b) => (new Date(a.date).getTime() - new Date(b.date).getTime())),
		cost: [...data.cost].sort((a, b) => (new Date(a.date).getTime() - new Date(b.date).getTime()))
	}

	// Формирования number списка
	const numberList = {
		time: sorted.time.map((item, index) => ({ name: `${item.date}_${item.task}`, number: index + 1 })),
		cost: sorted.cost.map((item, index) => ({ name: `${item.date}_${item.description}`, number: index + 1 }))
	};

	// Блоки идут слева направо: часы за весь период, расходы за весь период,
	// затем помесячные. Ширина блока зависит от типа таблицы, поэтому начало
	// каждого следующего считается от предыдущего, а не константой.
	const hoursAllColumn = 2,
		costsAllColumn = nextBlock(hoursAllColumn, "Hours"),
		hoursMonthColumn = nextBlock(costsAllColumn, "Costs"),
		costsMonthColumn = nextBlock(hoursMonthColumn, "Hours");

	// Создание таблицы Hours за весь период
	createTable({
		data,
		caption: t("timesheet_page.table.time"),
		period: "all",
		colStart: hoursAllColumn,
		rowStart: 7,
		type: "Hours",
		numberList
	});

	// Формирования данных для таблиц за конкретный период
	let counter = 0, timeLastLength: DataMap = {}, costLastLength: DataMap = {};
	const time = groupByMonthDescending(data.time),
		cost = groupByMonthDescending(data.cost);

	let prevValue = 0
	time.forEach((element, key) => {
		if (counter === 0) {
			timeLastLength[key] = element.length + 3;
			prevValue = element.length + 3;
		} else {
			timeLastLength[key] = prevValue + element.length + 4;
			prevValue = prevValue + element.length + 4;
		}

		counter++;
	})

	counter = 0;

	cost.forEach((element, key) => {
		if (counter === 0) {
			costLastLength[key] = element.length + 3;
			prevValue = element.length + 3;
		} else {
			costLastLength[key] = prevValue + element.length + 4;
			prevValue = prevValue + element.length + 4;
		}

		counter++;
	})

	const [timeLast, costLast] = alignDateObjects(timeLastLength, costLastLength);

	counter = 0;

	// Отрисовка таблиц time
	time.forEach((element, index) => {
		if (counter === 0) {
			createTable({
				data: { time: element as ITimeDetail[], cost: [] },
				caption: t("timesheet_page.table.time"),
				period: index,
				colStart: hoursMonthColumn,
				rowStart: 7,
				type: "Hours",
				numberList
			});
		} else {
			const prevElement = getPreviousEntry(timeLast, index);
			createTable({
				data: { time: element as ITimeDetail[], cost: [] },
				period: index,
				colStart: hoursMonthColumn,
				rowStart: prevElement ? 7 + prevElement[1] + 2 : 7 + 2,
				type: "Hours",
				numberList
			});
		}

		counter++;
	});

	counter = 0;

	// Отрисовка таблиц cost
	cost.forEach((element, index) => {
		
		if (counter === 0) {
			const prevElement = getPreviousEntry(timeLast, index);

			createTable({
				data: { time: [], cost: element as ICostDetail[] },
				caption: tr("Costs"),
				period: index,
				colStart: costsMonthColumn,
				rowStart: prevElement ? 7 + prevElement[1] + 1 : 7,
				type: "Costs",
				numberList
			});
		} else {
			const prevElement = getPreviousEntry(costLast, index);

			createTable({
				data: { time: [], cost: element as ICostDetail[] },
				period: index,
				colStart: costsMonthColumn,
				rowStart: prevElement ? prevElement[1] + 7 + 2 : 7 + 2,
				type: "Costs",
				numberList
			});
		}

		counter++;
	});

	// Отрисовка таблицы cost за весь период
	createTable({
		data,
		caption: tr("Cost"),
		period: "all",
		colStart: costsAllColumn,
		rowStart: 7,
		type: "Costs",
		numberList
	});
};